import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { RoutingPath } from 'mbs-ui-shared@constants';
import { Administrator, BrandingSetting, FeedBackCredential, OAuthCredential, TempTokenData } from 'mbs-ui-shared@models';
import { OAuthCodeParams } from 'mbs-ui-shared@models/OAuthCredential';
import TwoFactorComputers from 'mbs-ui-shared@models/TwoFactorComputers';
import {
  AuthCredentials,
  AuthGoogleCredentials,
  AuthIdCredentials,
  AuthMicrosoftCredentials,
  QrLoginResponse,
  TokenData
} from 'mbs-ui-shared@models/auth-models';
import { DataForLoginUser } from 'mbs-ui-shared@models/login';
import { OneTimeAuthToken } from 'mbs-ui-shared@models/one-time-token.model';
import { ConfigurationService } from 'mbs-ui-shared@services/configuration.service';
import { defer, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private http = inject(HttpClient);
  private httpBackend = inject(HttpBackend);
  private config = inject(ConfigurationService);

  private readonly mbsAuthUrlPrefix = 'api/auth/';
  private readonly mbsProviderUrlPrefix = 'api/provider/';
  private readonly mbsLoginPrefix = 'api/login';
  private readonly tokenDataLocalStorageKey = 'tokenData';

  // localStorage part

  public getTokenData(): OAuthCredential {
    return JSON.parse(localStorage.getItem(this.tokenDataLocalStorageKey));
  }

  public setTokenData(tokenData: OAuthCredential): void {
    localStorage.setItem(this.tokenDataLocalStorageKey, JSON.stringify(tokenData));
  }

  public removeTokenData(): void {
    localStorage.removeItem(this.tokenDataLocalStorageKey);
  }

  // requests part

  public authTokenBySessionRequest$(): Observable<OAuthCredential> {
    return new HttpClient(this.httpBackend).get<OAuthCredential>(`${location.origin}/${this.mbsAuthUrlPrefix}token`);
  }

  public oauthTokenBySessionRequest$(options: OAuthCodeParams): Observable<OAuthCredential> {
    return this.http.post<OAuthCredential>(`${this.mbsAuthUrlPrefix}oauth/token`, { ...options });
  }

  public fetchUserRequest$(): Observable<Administrator & { BrandingSetting: BrandingSetting }> {
    return this.http.get<Administrator & { BrandingSetting: BrandingSetting }>(`${this.mbsAuthUrlPrefix}me`);
  }

  public getRememberMeTime(): Observable<{ longTermSessionTimeSec: number; shortTermSessionTimeSec: number }> {
    return this.http.get<{ longTermSessionTimeSec: number; shortTermSessionTimeSec: number }>(`${this.mbsAuthUrlPrefix}remember-me/time`);
  }

  public applyRememberMe(): Observable<{ access_token: string; refresh_token: string }> {
    return this.http.post<{ access_token: string; refresh_token: string }>(`${this.mbsAuthUrlPrefix}remember-me`, {});
  }

  public localAgentLogout$(refreshToken: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.mbsAuthUrlPrefix}logout`, { refreshToken });
  }

  public appsTokenRequest$(request: { domain: string }): Observable<{ access_token: string }> {
    return this.http.post<{
      access_token: string;
    }>(`${this.mbsAuthUrlPrefix}new-token-scope/appsbackup-access`, request);
  }

  public permissionRequest$(payload: TwoFactorComputers): Observable<OAuthCredential> {
    return this.http.post<OAuthCredential>(`${this.mbsAuthUrlPrefix}new-token-scope/mbs-rm-access`, payload);
  }

  public getDropUserToken(): Observable<OAuthCredential> {
    return this.http.get<OAuthCredential>(`${this.mbsAuthUrlPrefix}token/mbs-drop-user`);
  }

  public loginRequest$({ email, password }): Observable<boolean> {
    return this.http.post(`${this.mbsLoginPrefix}/auth`, { email, password }).pipe(
      map(() => true),
      catchError(() => of(false))
    );
  }

  public loginByCredentialsInternal(credentials: AuthCredentials) {
    return this.http.post<TokenData>(`${this.mbsLoginPrefix}/credentials/internal`, credentials, {
      headers: {
        CustomAuth: 'true'
      }
    });
  }

  public loginByCredentials(credentials: AuthCredentials) {
    return this.http.post<TokenData>(`${this.mbsLoginPrefix}/credentials`, credentials, {
      headers: {
        CustomAuth: 'true'
      }
    });
  }

  public loginUserByCredentials(credentials: AuthCredentials, data: DataForLoginUser) {
    return this.http.post<TokenData>(
      `${this.mbsLoginPrefix}/user/credentials`,
      { ...credentials, ...data },
      {
        headers: {
          CustomAuth: 'true'
        }
      }
    );
  }

  public loginByGoogle(credentials: AuthGoogleCredentials) {
    return this.http.post<TokenData>(`${this.mbsLoginPrefix}/google`, credentials, {
      headers: {
        CustomAuth: 'true'
      }
    });
  }

  public loginUserByGoogle(credentials: AuthGoogleCredentials) {
    return this.http.post<TokenData>(`${this.mbsLoginPrefix}/user/google`, credentials, {
      headers: {
        CustomAuth: 'true'
      }
    });
  }

  public loginByMicrosoft(credentials: AuthMicrosoftCredentials) {
    return this.http.post<TokenData>(`${this.mbsLoginPrefix}/microsoft`, credentials, {
      headers: {
        CustomAuth: 'true'
      }
    });
  }

  public loginUserByMicrosoft(credentials: AuthMicrosoftCredentials) {
    return this.http.post<TokenData>(`${this.mbsLoginPrefix}/user/microsoft`, credentials, {
      headers: {
        CustomAuth: 'true'
      }
    });
  }

  public loginByAdminToken(token: string) {
    return this.http.post<TokenData>(
      `${this.mbsLoginPrefix}/super-admin`,
      { token },
      {
        headers: {
          CustomAuth: 'true'
        }
      }
    );
  }

  public loginByAuthId(credentials: AuthIdCredentials) {
    return this.http.post<TokenData>(`${this.mbsLoginPrefix}/authorize`, credentials, {
      headers: {
        CustomAuth: 'true'
      }
    });
  }

  public oneTimeAuthTokenRequest$(): Observable<OneTimeAuthToken> {
    return this.http.get<OneTimeAuthToken>(`${this.mbsAuthUrlPrefix}onetimetoken`);
  }

  public checkAdminToken(): Observable<boolean> {
    return this.http.get<boolean>(`${this.mbsAuthUrlPrefix}checkadmintoken`);
  }

  public getAgentLocalBaseUrl(): Observable<{ endpoint: string }> {
    return this.http.get<{ endpoint: string }>(`/api/computer/endpoint`);
  }

  public feedBackTokenRequest$() {
    return this.http.get<FeedBackCredential>(`${this.mbsAuthUrlPrefix}new-token-scope/feedback-access`, {
      headers: { 'x-front-queue': 'true' }
    });
  }

  public changeEmailRequest$(value: { email: string; password: string }) {
    return this.http.put(`${this.mbsProviderUrlPrefix}email`, value);
  }

  public allowOfflineEditRequest$(state = true): Observable<null> {
    return this.http.put<null>(`${this.mbsAuthUrlPrefix}offlineedit`, { allowOfflineEdit: state });
  }

  // TODO: move to user service ?

  public tempTokenRequest$(userId: string, data: { tokenTarget: string; computerHid?: string }): Observable<TempTokenData> {
    return this.http.post<TempTokenData>(`api/users/${userId}/temptoken`, data);
  }

  // url part

  public getSupportPortalUrl(token: OneTimeAuthToken, path?: string): string {
    const returnUrl = `${location.origin}${RoutingPath.ApComputers}`;

    return `${this.config.get('supportUrl')}${path || ''}?OneTimeAuthToken=${encodeURIComponent(
      token.access_token
    )}&MbsReturnUrl=${returnUrl}`;
  }

  // legacy

  public prepareSubscriptionUrl(ownerId: string): string {
    return `/Admin/payment.aspx?type=Subscription&OwnerID=${ownerId}&customPrice=1&InvoiceID=`;
  }

  public logout() {
    return this.http.post('api/logout', null, { responseType: 'text' });
  }

  public logoutOneTimeSession(authToken: string): Observable<unknown> {
    return defer(() =>
      fetch(new URL('api/logout', document.baseURI).href, {
        method: 'POST',
        keepalive: true,
        credentials: 'omit',
        headers: { Authorization: authToken, 'X-Requested-With': 'XMLHttpRequest' }
      })
    );
  }

  public getQrCode(): Observable<QrLoginResponse> {
    return this.http.get<QrLoginResponse>(`${this.mbsLoginPrefix}/qr`);
  }

  public loginByQr(rememberMe: boolean): Observable<null> {
    return this.http.post<null>(`${this.mbsLoginPrefix}/qr`, { rememberMe });
  }
}
